/**
 * @module app.tooltipDirective
 */
import {buffer, createEmpty, extend} from "ol/extent";
import appBase from './index.js';
import {MAIN_MENU_EVENT_NAME, MENU_TOGGLE_COLLAPSE} from './constants.js';
import {
  objectForEach,
  isExternalLink,
  capitalize,
  isNumber,
  isWinterLayer,
  NO_HTTP_CREDENTIALS, QUERY_HIGHLIGHT_ID
} from './utils.js';
import {remove as olArrayRemove} from 'ol/array';
import {appendParams} from 'ol/uri';
import olFeature from 'ol/Feature.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';


/**
 * @type {Object.<string, string>}
 * @const
 */
export const SBB_URL_BY_LANG = {
  'de': 'https://www.sbb.ch/de/kaufen/pages/fahrplan/fahrplan.xhtml',
  'en': 'https://www.sbb.ch/en/buying/pages/fahrplan/fahrplan.xhtml',
  'fr': 'https://www.sbb.ch/fr/acheter/pages/fahrplan/fahrplan.xhtml',
  'it': 'https://www.sbb.ch/it/acquistare/pages/fahrplan/fahrplan.xhtml'
};

/**
 * @param {angular.Scope} $scope Scope.
 * @param {jQuery} $element Element.
 * @param {angular.$http} $http Angular http service.
 * @param {angular.$filter} $filter Filter service.
 * @param {app.MapService} appMapService
 * @param {angularGettext.Catalog} gettextCatalog Gettext catalog.
 * @param {app.HighlightFeature} appHighlightFeature
 * @param {ngeo.statemanager.Location} ngeoLocation ngeo Location service.
 * @param {string} apiUrl
 * @param {app.detour.Service} appDetourService
 * @param {app.CesiumLoader} appCesiumLoader
 * @constructor
 * @ngInject
 * @private
 */
class TooltipController {

  constructor($scope, $element, $http, $filter, appMapService,
    gettextCatalog, appHighlightFeature, ngeoLocation, apiUrl, appDetourService,
    appCesiumLoader) {

    /**
     * @type {string}
     * @export
     */
    this.rootCssClass;

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @private
     */
    this.mainCtrl_ = $scope.$root['mainCtrl'];

    /**
     * @private
     */
    this.cesium_ = appCesiumLoader;

    /**
     * @type {ol.Map}
     * @private
     */
    this.map_ = appMapService.map;

    /**
     * @type {angular.$http}
     * @private
     */
    this.http_ = $http;

    /**
     * @type {function(Date, string) : string}
     * @private
     */
    this.dateFormat_ = $filter('date');

    /**
     * @type {angularGettext.Catalog}
     */
    this.gettextCatalog = gettextCatalog;

    /**
     * @type {app.HighlightFeature}
     * @private
     */
    this.appHighlight_ = appHighlightFeature;
    console.assert(appHighlightFeature.initialized);

    /**
     * @type {ol.Feature}
     * @private
     */
    this.highlightedFeature_ = null;

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.format_ = new olFormatGeoJSON();

    /**
     * @type {jQuery}
     * @private
     */
    this.menuCollapser_ = $element.find('[app-collapse-menu]');

    /**
     * @type {jQuery}
     * @private
     */
    this.menuBody_ = $element.find('.menu-item-body');

    /**
     * @type {boolean}
     * @private
     */
    this.showTooltipPending_ = false;

    /**
     * @type {ngeo.statemanager.Location}
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * @type {string}
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @private
     */
    this.appDetourService_ = appDetourService;

    /**
     * @type {string|undefined}
     * @export
     */
    this.contentTemplateUrl;

    /**
     * @type {?string}
     * @export
     */
    this.titleText = null;

    /**
     * @type {Object}
     */
    this['attributes'] = {};

    /**
     * @type {Function}
     */
    this.isExternalLink = isExternalLink;

    /**
     * @type {Object}
     */
    this['bbs'] = {};
    this.scope_.$on('featureSelected', (event, feature) => {
      this.appHighlight_.clear();

      let skipHighlight = false;

      const properties = feature['properties'];
      const type = properties['type'];
      if (type) {
        if (type === 'track' || type === 'track_poi') {
          return;
        }
        if (type === 'detour') {
          skipHighlight = true;
          this.appDetourService_.highlightFeatures(properties['id']);
          // select first activated land from the land list
          const activeLayers = this.mainCtrl_.activeLayers();
          let land = '';
          let found = false;
          objectForEach(properties['land'].split(','), (landname, id) => {
            if (!found) {
              objectForEach(activeLayers, (layerval, layername) => {
                if (!found && layername.toLowerCase().includes(landname)) {
                  land = landname;
                  found = true;
                }
              });
            }
          });
          properties['land'] = land;
          this.appDetourService_.toggleProtectedHighlight(true);
        }
        if (type === 'Mountaineering') {
          if (properties.sac_photos) {
            const sacPhoto = properties.sac_photos.find(p => !!p.photo_small);
            if (sacPhoto) {
              properties['photo'] = sacPhoto.photo_small;
            }
          }
          if (properties.abstract && properties.abstract.length > 350) {
            properties.abstract = `${properties.abstract.slice(0, 347)}...`;
          }
          properties.difficulty =
              properties.mountain_hiking_difficulty ||
              properties.alpine_tour_difficulty ||
              properties.alpine_climbing_difficulty ||
              properties.via_ferrata_difficulty ||
              properties.ski_tour_difficulty;
        }
      }
      const name = properties['sl_hlname'];
      if (name === 'slowup') {
        skipHighlight = true;
      }

      const geom = this.format_.readGeometry(feature['geometry']);

      if (geom && !skipHighlight) {
        properties['type'] = properties['type'] ? properties['type'] : QUERY_HIGHLIGHT_ID;
        const hlId = this.createFeatureId_(properties['type'], feature['id']);
        this.highlightedFeature_ = new olFeature(geom);
        const layers = properties['sl_layers'];
        const config = {
          'isProtected': false,
          'e_number': properties['e_number'],
          'signal_way': properties['signal_way'],
          'route_signal_way': properties['route_signal_way']
        };
        if (layers && layers.length) {
          config['layer'] = layers[0];
        }
        if (feature['geometry']['type'] === 'MultiLineString') {
          config['kind'] = 'transparent';
        } else {
          // special case for alpentaxi
          const category_alpentaxi = feature['properties']['category_alpentaxi'];
          if (category_alpentaxi) {
            config['kind'] = category_alpentaxi;
          } else {
            config['kind'] = feature['properties']['sl_hlname'];
          }
        }
        this.appHighlight_.addFeature(this.highlightedFeature_, hlId, config);
      }

      this.setTemplateInfo_(properties);
      if (this.attributes.date_start && this.attributes.date_end) {
        const dateStart = new Date(this.attributes.date_start);
        const dateEnd = new Date(this.attributes.date_end);
        if (!isNaN(dateStart.getDate())) {
          this.attributes.date_start = this.dateFormat_(dateStart, 'dd.MM.yyyy');
        }
        if (!isNaN(dateStart.getDate())) {
          this.attributes.date_end = this.dateFormat_(dateEnd, 'dd.MM.yyyy');
        }
      }

      // open the tooltip menu
      this.openTooltip_();

      this.isNumber = isNumber;
    });

    this.scope_.$on('submenuChange', (event, data) => {
      if (this.menuCollapser_.is(data.element) && data.open === false && !this.showTooltipPending_) {
        this.closeTooltip_();
      }
    });

    this.scope_.$on(MAIN_MENU_EVENT_NAME.MAIN_MENU_SHOWN, (event) => {
      if (this.showTooltipPending_) {
        this.openTooltip_();
      }
    });
  }


  /**
   * @param {Object} properties Properties of the feature to d.
   * @private
   */
  setTemplateInfo_(properties) {
    if (!properties) {
      this.titleText = null;
      return;
    }
    this.titleText = properties['sl_name'];

    const baseURL = 'partials/tooltip/';
    const category = properties['category'];
    const land = properties['land'];

    let featureType = properties['type']; // FIXME: check if all features have a type

    if (featureType === 'Ort' || featureType === 'Sight' || category === 'local' ||
        category === 'handicap' || featureType === 'etappen') {
      // don't display tel or e-mail
      delete properties['tel'];
      delete properties['email'];
    }

    if (category === 'accomodation' ||
        category === 'alpentaxi' ||
        category === 'stations' ||
        category === 'swisspark' ||
        category === 'slowup') {
      featureType = category;
    }

    this.contentTemplateUrl = baseURL + this.getTemplate_(featureType, land);

    if (featureType === 'etappen' && land && land !== 'all' && !land.includes(',')) {
      this.rootCssClass = land.replace('land', '');
    } else {
      switch (featureType) {
        case 'alpentaxi':
          this.rootCssClass = 'alpentaxi';
          break;
        case 'slowup' :
          this.rootCssClass = 'slowup';
          break;
        case 'stations':
          this.rootCssClass = 'bbs';
          break;
        case 'wildlife':
          this.rootCssClass = 'wildlife';
          break;
        case 'alpguarddog':
          this.rootCssClass = 'alpguarddog';
          break;
        case 'detour':
          this.rootCssClass = land.replace('land', '');
          if (!this.rootCssClass.length) {
            this.rootCssClass = 'wander';
          }
          break;
        case 'Mountaineering':
          this.rootCssClass = 'mountaineering';
          break;
        default:
          this.rootCssClass = 'default';
          break;
      }
    }

    if (featureType === 'stations') {
      // Form values are not reset if a station was previously loaded.
      // This allows to change the station without reinitializing the form.
      const now = new Date(Date.now());
      this['bbs']['from'] = this['bbs']['from'] || '';
      this['bbs']['to'] = properties['sl_title'];
      this['bbs']['date'] = this['bbs']['date'] || this.dateFormat_(now, 'dd.MM.yyyy');
      this['bbs']['time'] = this['bbs']['time'] || this.dateFormat_(now, 'HH:mm');
      this['bbs']['atDeparture'] = this['bbs']['atDeparture'] !== undefined ? this['bbs']['atDeparture'] : 1;
      this['bbs']['withVelo'] = this['bbs']['withVelo'] || false;
    }

    this.attributes = properties;

    if (featureType === 'etappen' && isWinterLayer(land)) {
      const url = properties['stn_url'];
      this.http_.get(url, NO_HTTP_CREDENTIALS).then(response => {
        if (response.status === 200 && response.data.routes.routesArray.length === 1) {
          const data = response.data.routes.routesArray[0];
          if (data.status.content) {
            this.attributes.stn_status = data.status.content;
          }
          if (data.isStationActive.content === 1) {
            this.attributes.stn_link = data.link.content;
          }
        }
      });
    }
  }

  /**
   * @private
   * @param {string} featureType
   * @param {string|undefined} land
   */
  getTemplate_(featureType, land) {
    if (featureType === 'etappen') {
      if (land) {
        switch (land) {
          case 'veloland':
            return 'facts-veloland.html';
          case 'wanderland':
            return 'facts-wanderland.html';
          case 'mtbland':
            return 'facts-mtbland.html';
          case 'winterhiking':
            return 'facts-winterhiking.html';
          case 'snowshoe':
            return 'facts-snowshoe.html';
          case 'sledging':
            return 'facts-sledging.html';
          case 'crosscountry':
            return 'facts-crosscountry.html';
          default:
            break;
        }
      }
      return 'facts-default.html';
    } else {
      switch (featureType) {
        case 'alpentaxi':
          return 'alpentaxi.html';
        case 'slowup' :
          return 'facts-default.html';
        case 'accomodation':
          return 'accommodation.html';
        case 'Rental':
          return 'velorental.html';
        case 'Service':
          return 'veloservice.html';
        case 'stations':
          return 'bbs.html';
        case 'wildlife':
          return 'wildlife.html';
        case 'alpguarddog':
          return 'alpguarddog.html';
        case 'detour':
          return 'detour.html';
        case 'Mountaineering':
          return 'mountaineering.html';
        default:
          return 'default.html';
      }
    }
  }

  /**
   * Open the tooltip menu.
   * @private
   */
  openTooltip_() {
    // Reset any pending request
    this.showTooltipPending_ = false;
    if (!this.menuBody_.is(':visible')) {
      // back to the main menu (if needed)
      if ($('.menu-button-collapse-sub-menu').is(':visible')) {
        // ask for tooltip opening when main menu is done opening
        this.showTooltipPending_ = true;
        // open the main menu
        this.scope_.$emit(MAIN_MENU_EVENT_NAME.COLLAPSE_SUB_MENU, {no_animation: true, showMainMenu: true});
      } else {
        // in case the whole menu is hidden, hide the open button
        $('[app-expand-main-menu]').addClass('hidden');
        // show the tooltip menu
        this.scope_.$emit(MENU_TOGGLE_COLLAPSE, 'tooltip-menu');
      }
    }
  }


  /**
   * Close the tooltip menu.
   * @private
   */
  closeTooltip_() {
    this.setTemplateInfo_(null);
    this.appHighlight_.clear();
    this.appDetourService_.toggleProtectedHighlight(false);
    this.appDetourService_.unhighlightFeatures();
  }


  /**
   * @private
   * @param {ol.Feature} feature
   * @return {boolean}
   */
  highlightIsProtected_(feature) {
    return (feature) ? !!feature.get('isProtected') : false;
  }


  /**
   * @private
   * @param {string} hlId highlight id
   */
  toggleHighlightProtection_(hlId) {
    const feature = this.appHighlight_.getFeatureById(hlId);
    if (feature) {
      if (this.highlightIsProtected_(feature)) {
        this.appHighlight_.setFeature(feature, {'isProtected': false});
        this.appHighlight_.clear();
        this.updateHighlightPermalink_(feature, false);
      } else {
        this.appHighlight_.setFeature(feature, {'isProtected': true});
        this.updateHighlightPermalink_(feature, true);
      }
    }
  }


  /**
   * @private
   * @param {ol.Feature} feature
   * @param {boolean} add True if feature is added, false if removed
   */
  updateHighlightPermalink_(feature, add) {
    let highlight = this.ngeoLocation_.getParam('highlight');
    const items = {};
    if (highlight) {
      const highlightPairs = highlight.split(',');
      for (let j = 0, jj = highlightPairs.length; j < jj; j++) {
        const highlightValue = highlightPairs[j].split('*');
        console.assert(highlightValue.length === 2);
        items[highlightValue[0]] = highlightValue[1].split('-');
      }
    }
    let layer = feature.get('layer');
    let id = feature.get('e_number');
    if (id === undefined) {
      id = feature.get('r_number');
      layer = layer.replace('Etappen', 'Routen');
    }
    console.assert(layer);
    console.assert(id);
    if (add) {
      items[layer] = items[layer] || [];
      if (!items[layer].includes(id.toString())) {
        items[layer].push(id.toString());
      }
    } else if (layer in items) {
      olArrayRemove(items[layer], id.toString());
      if (items[layer].length === 0) {
        delete items[layer];
      }
    }
    const list = [];
    for (layer in items) {
      list.push(layer + '*' + items[layer].join('-'));
    }
    if (list.length > 0) {
      highlight = list.join(',');
      this.ngeoLocation_.updateParams({'highlight': highlight});
    } else {
      this.ngeoLocation_.deleteParam('highlight');
    }
  }


  /**
   * For etappen tooltip
   * @export
   * @return {boolean}
   */
  etappenHighlightIsProtected() {
    const hlId = '' + this.highlightedFeature_.getId();
    const feature = this.appHighlight_.getFeatureById(hlId);
    return this.highlightIsProtected_(feature);
  }


  /**
   * For etappen tooltip
   * @export
   */
  toggleEtappenHighlightProtection() {
    const hlId = '' + this.highlightedFeature_.getId();
    const feature = this.appHighlight_.getFeatureById(hlId);
    if (!feature) {
      const layerName = this.getLayerNameForStages_();
      const config = {
        'kind': 'transparent',
        'isProtected': false,
        'layer': layerName
      };
      this.appHighlight_.addFeature(this.highlightedFeature_, hlId, config);
    }
    this.toggleHighlightProtection_(hlId);
  }


  /**
   * For etappen (routen) tooltip
   * @export
   * @return {boolean}
   */
  routenHighlightIsProtected() {
    const feature = this.appHighlight_.getFeatureById(this.getRouteHighlightId_());
    return this.highlightIsProtected_(feature);
  }


  /**
   * For etappen (routen) tooltip
   * @export
   */
  toggleRoutenHighlightProtection() {
    const hlId = this.getRouteHighlightId_();
    const feature = this.appHighlight_.getFeatureById(hlId);
    if (!feature) {
      this.getRouteFeature_();
    } else {
      this.toggleHighlightProtection_(hlId);
    }
  }


  /**
   * For etappen (routen) tooltip
   * @private
   * @return {string}
   */
  getRouteHighlightId_() {
    const routeNumber = parseInt(this['attributes']['e_number'], 10);
    const routeName = this.getLayerNameForStages_().replace('Routen', 'Etappen');
    return routeName + routeNumber + '_tooltip';
  }


  /**
   * For etappen (routen) tooltip
   * @private
   * @return {string}
   */
  getLayerNameForStages_() {
    const land = capitalize(this['attributes']['land']);
    let category = capitalize(this['attributes']['category']);
    if (category === 'Local') {
      category = 'Lokal';
    }
    return land + 'Etappen' + category;
  }


  /**
   * For etappen (routen) tooltip
   * @param {?function(Array.<ol.Feature>)=} opt_cb Optional callback.
   * @private
   */
  getRouteFeature_(opt_cb) {
    const routeNumber = '' + parseInt(this['attributes']['e_number'], 10);
    const layerName = this.getLayerNameForStages_();
    const hlId = this.getRouteHighlightId_();
    let url = this.apiUrl_ + '4/query/featuresmultilayers';
    url += '?' + layerName.replace('Etappen', 'Routen') + '=' + routeNumber;

    this.http_.get(url).then((response) => {
      const features = response['data']['features'] || [];
      if (features.length > 0) {
        const olFeature = this.format_.readFeature(features[0]);
        if (olFeature) {
          // The layer name returned by the service ends with 'Local' but
          // we expect 'Lokal' (backward compatibility)
          olFeature.set('layer', olFeature.get('layer').replace('Local', 'Lokal'));
          const config = {
            'kind': 'transparent',
            'isProtected': true,
            'layer': layerName,
            'r_number': routeNumber
          };
          console.assert(olFeature.get('r_number') == routeNumber);
          this.appHighlight_.addFeature(olFeature, hlId, config);
          this.updateHighlightPermalink_(olFeature, true);
          if (opt_cb) {
            opt_cb([olFeature]);
          }
        }
      }
    });
  }


  /**
   * @private
   * @param {string} wdbType
   * @param {string} wdbId
   * @return {string}
   */
  createFeatureId_(wdbType, wdbId) {
    return wdbType + '#' + wdbId;
  }


  /**
   * For bbs
   * @export
   */
  toggleBbsFromTo() {
    const from = this['bbs']['from'];
    this['bbs']['from'] = this['bbs']['to'];
    this['bbs']['to'] = from;
  }


  /**
   * For bbs
   * @export
   * @param {boolean} isAtDeparture
   */
  setBbsAtDeparture(isAtDeparture) {
    const abElement = this.menuBody_.find('.bbs-ab');
    const anElement = this.menuBody_.find('.bbs-an');
    if (isAtDeparture) {
      this['bbs']['atDeparture'] = 1;
      abElement.addClass('selected');
      anElement.removeClass('selected');
    } else {
      this['bbs']['atDeparture'] = 0;
      abElement.removeClass('selected');
      anElement.addClass('selected');
    }
  }


  /**
   * For bbs
   * @export
   */
  bbsSubmit() {
    const url = SBB_URL_BY_LANG[this.gettextCatalog.currentLanguage];
    const params = {
      'von': this['bbs']['from'],
      'nach': this['bbs']['to'],
      'an': this['bbs']['atDeparture'] ? 'false' : 'true',
      'datum': this['bbs']['date'],
      'zeit': this['bbs']['time'],
      'suche': 'true',
      'velo': this['bbs']['withVelo'] ? 'true' : 'false',
    };

    window.open(appendParams(url, params), '_blank');
  }

  /**
   * Recenter on feature and open print dialog.
   * @param {Array.<ol.Feature>} features Features to print.
   * @private
   */
  printFeature_(features) {
    if (this.map_) {
      // open print menu
      this.mainCtrl_.showMenu('print', true);

      // fit map to features (with buffer)
      let extent = createEmpty();
      objectForEach(features, feature => {
        extend(extent, feature.getGeometry().getExtent());
      });
      //var extent = /** @type {!ol.Extent} */ (feature.getGeometry().getExtent());
      const largerEdge = Math.max(extent[2] - extent[0], extent[3] - extent[1]);
      extent = buffer(extent, largerEdge * 0.3); // add 30% buffer
      const size = /** @type {!ol.Size} */ (this.map_.getSize());
      this.map_.getView().fit(extent, size);

      // emit submenuChange event to trigger print.togglePrint_
      // which resizes scale and visualization of the printable area
      this.scope_.$root.$broadcast('submenuChange', {
        'open': true,
        'element': $("#print [app-collapse-menu]")
      });
      this.closeTooltip_();
    }
  }

  /**
   * Print entire route.
   * @export
   */
  printRoute() {
    if (!this.cesium_.not3d()) {
      // no available when 3d is active
      return;
    }
    const hlId = this.getRouteHighlightId_();
    const feature = this.appHighlight_.getFeatureById(hlId);
    if (!feature) {
      this.getRouteFeature_(this.printFeature_.bind(this));
    } else {
      this.printFeature_([feature]);
    }
  }

  /**
   * Print current stage of route.
   * @export
   */
  printDetour() {
    if (!this.cesium_.not3d()) {
      // no available when 3d is active
      return;
    }
    this.appDetourService_.getHighlitedFeatures().then(highlitedFeatures => {
      this.printFeature_(highlitedFeatures);
    });
  }

  /**
   * Print current stage of route.
   * @export
   */
  printStage() {
    if (!this.cesium_.not3d()) {
      // no available when 3d is active
      return;
    }
    const hlId = '' + this.highlightedFeature_.getId();
    const feature = this.appHighlight_.getFeatureById(hlId);
    if (!feature) {
      const layerName = this.getLayerNameForStages_();
      const config = {
        'kind': 'transparent',
        'isProtected': true,
        'layer': layerName
      };
      this.appHighlight_.addFeature(this.highlightedFeature_, hlId, config);
    } else {
      feature.set('isProtected', true);
    }
    this.printFeature_([this.highlightedFeature_]);
  }

  /**
   * @return {boolean} True if tooltip is a single stage.
   * @export
   */
  isStage() {
    return this['attributes'].type === 'etappen'; //
  }

  /**
   * @return {boolean} True if the segment belongs to a oneway mtb route.
   * @export
   */
  isOneWay() {
    return this['attributes']['route_signal_way'] === 'oneway';
  }
}

appBase.controller('appTooltipController', TooltipController);

/**
 * @return {angular.Directive} The directive specs.
 * @ngInject
 */
const appTooltip = function() {
  return {
    restrict: 'E',
    controller: 'appTooltipController',
    controllerAs: 'tooltipCtrl',
    bindToController: true,
    templateUrl: 'partials/tooltip.html'
  };
};

appBase.directive('appTooltip', appTooltip);


export default appTooltip;
